import { useState, type PropsWithChildren } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiClientError, apiClient } from '../api';
import { useOnlineStatus, useVisitQueue } from '../hooks';
import { clearOfflineData, visitQueue } from '../offline';
import type { Campaign, CampaignRole, Me } from '../types';
import { StatusPill } from './Common';

export type AppPage = 'work' | 'runs' | 'dashboard' | 'admin' | 'print';

export function AppShell({
  me,
  campaign,
  campaigns,
  page,
  role,
  onPage,
  onCampaign,
  onOpenSync,
  children,
}: PropsWithChildren<{
  me: Me;
  campaign: Campaign;
  campaigns: Campaign[];
  page: AppPage;
  role: CampaignRole;
  onPage: (page: AppPage) => void;
  onCampaign: (campaignId: string) => void;
  onOpenSync: () => void;
}>) {
  const online = useOnlineStatus();
  const queue = useVisitQueue();
  const queryClient = useQueryClient();
  const [pendingSignOut, setPendingSignOut] = useState<{
    scope: 'device' | 'everywhere';
    count: number;
  } | null>(null);
  const [checkingSignOut, setCheckingSignOut] = useState(false);
  const [signOutError, setSignOutError] = useState<unknown>(null);
  const finishSignOut = async () => {
    await clearOfflineData();
    queryClient.clear();
    window.location.replace('/');
  };
  const handleSignOutError = (error: unknown) => {
    if (
      error instanceof ApiClientError &&
      ['authentication_required', 'session_expired'].includes(error.code)
    ) {
      void finishSignOut();
      return;
    }
    setSignOutError(error);
  };
  const logout = useMutation({
    mutationFn: apiClient.logout,
    onSuccess: finishSignOut,
    onError: handleSignOutError,
  });
  const revokeAll = useMutation({
    mutationFn: apiClient.revokeAllSessions,
    onSuccess: finishSignOut,
    onError: handleSignOutError,
  });
  const performSignOut = (scope: 'device' | 'everywhere') => {
    setSignOutError(null);
    if (scope === 'everywhere') revokeAll.mutate();
    else logout.mutate();
  };
  const requestSignOut = async (scope: 'device' | 'everywhere') => {
    setCheckingSignOut(true);
    setSignOutError(null);
    try {
      const currentQueue = await visitQueue();
      if (currentQueue.length > 0) {
        setPendingSignOut({ scope, count: currentQueue.length });
      } else performSignOut(scope);
    } catch (error) {
      setSignOutError(error);
    } finally {
      setCheckingSignOut(false);
    }
  };
  const pending = (queue.data ?? []).filter((record) =>
    ['pending', 'syncing', 'failed'].includes(record.state),
  ).length;
  const coordinator = role === 'coordinator' || role === 'admin';
  return (
    <div
      className="app"
      style={
        {
          '--campaign-primary': campaign.branding.primaryColour,
          '--campaign-accent': campaign.branding.accentColour,
          '--campaign-highlight': campaign.branding.highlightColour,
          '--campaign-bg': campaign.branding.backgroundColour,
          '--campaign-text': campaign.branding.textColour,
        } as React.CSSProperties
      }
    >
      <header className="app-header">
        <div className="brand-lockup">
          <img src="/icon.svg" width="40" height="40" alt="" />
          <div>
            <span>Flyer operations</span>
            <strong>{campaign.publicLabel}</strong>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="sync-button"
            type="button"
            onClick={onOpenSync}
            aria-label={`${pending} pending updates; ${online ? 'online' : 'offline'}`}
          >
            <span className={`connection-dot ${online ? 'online' : 'offline'}`} />
            {pending > 0 ? `${pending} pending` : online ? 'Synced' : 'Offline'}
          </button>
          <details className="user-menu">
            <summary aria-label="Open account menu">
              {me.user.displayName
                .split(/\s+/u)
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
                .toLocaleUpperCase('en-NZ')}
            </summary>
            <div>
              <strong>{me.user.displayName}</strong>
              <span>{role}</span>
              {campaigns.length > 1 ? (
                <label>
                  Campaign
                  <select value={campaign.id} onChange={(event) => onCampaign(event.target.value)}>
                    {campaigns.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                        {item.status === 'active' ? '' : ` (${item.status})`}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <button
                type="button"
                onClick={() => void requestSignOut('device')}
                disabled={checkingSignOut || logout.isPending || revokeAll.isPending}
              >
                Sign out
              </button>
              <button
                type="button"
                onClick={() => void requestSignOut('everywhere')}
                disabled={checkingSignOut || logout.isPending || revokeAll.isPending}
              >
                Sign out everywhere
              </button>
              {signOutError instanceof Error ? (
                <span role="alert">{signOutError.message}</span>
              ) : null}
            </div>
          </details>
        </div>
      </header>
      {!online ? (
        <div className="offline-banner" role="status">
          <StatusPill tone="warning">Offline</StatusPill>
          Cached work remains available. Saved visits will sync later.
        </div>
      ) : null}
      <main id="main-content" className="app-main">
        {children}
      </main>
      <nav className="bottom-nav" aria-label="Main navigation">
        {role !== 'viewer' ? (
          <button
            className={page === 'work' ? 'active' : ''}
            type="button"
            onClick={() => onPage('work')}
            aria-current={page === 'work' ? 'page' : undefined}
          >
            <span aria-hidden="true">✓</span>
            Work
          </button>
        ) : null}
        {role !== 'viewer' ? (
          <button
            className={page === 'runs' ? 'active' : ''}
            type="button"
            onClick={() => onPage('runs')}
            aria-current={page === 'runs' ? 'page' : undefined}
          >
            <span aria-hidden="true">♟</span>
            Runs
          </button>
        ) : null}
        <button
          className={page === 'dashboard' ? 'active' : ''}
          type="button"
          onClick={() => onPage('dashboard')}
          aria-current={page === 'dashboard' ? 'page' : undefined}
        >
          <span aria-hidden="true">▥</span>
          Progress
        </button>
        {coordinator ? (
          <button
            className={page === 'admin' ? 'active' : ''}
            type="button"
            onClick={() => onPage('admin')}
            aria-current={page === 'admin' ? 'page' : undefined}
          >
            <span aria-hidden="true">⚙</span>
            {role === 'admin' ? 'Admin' : 'Coordinate'}
          </button>
        ) : null}
      </nav>
      {pendingSignOut ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onMouseDown={() => setPendingSignOut(null)}
        >
          <section
            className="bottom-sheet"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="sign-out-title"
            aria-describedby="sign-out-description"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-heading">
              <div>
                <p className="eyebrow">Unsynced work</p>
                <h2 id="sign-out-title">Discard updates and sign out?</h2>
              </div>
              <button
                className="icon-button"
                type="button"
                onClick={() => setPendingSignOut(null)}
                aria-label="Cancel sign out"
              >
                ×
              </button>
            </div>
            <p id="sign-out-description">
              {pendingSignOut.count} update{pendingSignOut.count === 1 ? ' is' : 's are'} still on
              this device. Signing out removes {pendingSignOut.count === 1 ? 'it' : 'them'}{' '}
              permanently to protect the next user.
            </p>
            <div className="button-row">
              <button
                className="button button-primary"
                type="button"
                onClick={() => {
                  setPendingSignOut(null);
                  onOpenSync();
                }}
              >
                Review updates
              </button>
              <button
                className="button button-danger-text"
                type="button"
                onClick={() => {
                  const { scope } = pendingSignOut;
                  setPendingSignOut(null);
                  performSignOut(scope);
                }}
              >
                Discard updates and sign out
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
